/**
 * Hash-router minimaliste typé.
 *
 * Lit la location.hash (ex: "#/profils/guerrier?famille=combattants"), en
 * extrait un {path, params, query} structuré, et pousse des updates via un
 * signal observable.
 *
 * Les routes valides sont **déclarées** dans `ROUTES` : pour ajouter une page,
 * ajouter ici + implémenter le handler côté shell (`src/main.ts`).
 */

import { signal } from '@state/signal';

export type RouteId =
  | 'accueil'
  | 'profils'
  | 'profil'
  | 'peuples'
  | 'peuple'
  | 'prestige'
  | 'equipement'
  | 'bestiaire'
  | 'creature'
  | 'compagnons'
  | 'religions'
  | 'regles'
  | 'aventure'
  | 'univers'
  | 'glossaire'
  | 'personnage'
  | 'personnage-new'
  | 'personnage-fiche'
  | 'campagne'
  | 'campagne-lobby'
  | 'montee-niveau'
  | 'table'
  | 'mj'
  | 'options'
  | 'login'
  | 'dashboard'
  | 'not-found';

export interface RouteDef {
  readonly id: RouteId;
  /** Pattern avec paramètres `:name`. Racine seule = `''`. */
  readonly pattern: string;
}

export const ROUTES: readonly RouteDef[] = [
  { id: 'accueil', pattern: '' },
  { id: 'accueil', pattern: '/' },
  { id: 'profils', pattern: '/profils' },
  { id: 'profil', pattern: '/profils/:id' },
  { id: 'peuples', pattern: '/peuples' },
  { id: 'peuple', pattern: '/peuples/:id' },
  { id: 'prestige', pattern: '/prestige' },
  { id: 'equipement', pattern: '/equipement' },
  { id: 'bestiaire', pattern: '/bestiaire' },
  { id: 'creature', pattern: '/bestiaire/:id' },
  { id: 'compagnons', pattern: '/compagnons' },
  { id: 'religions', pattern: '/religions' },
  { id: 'regles', pattern: '/regles' },
  { id: 'aventure', pattern: '/aventure' },
  { id: 'univers', pattern: '/univers' },
  { id: 'glossaire', pattern: '/glossaire' },
  { id: 'personnage', pattern: '/personnage' },
  { id: 'personnage-new', pattern: '/personnage/new' },
  { id: 'personnage-fiche', pattern: '/personnage/:id' },
  { id: 'campagne', pattern: '/campagne' },
  { id: 'campagne-lobby', pattern: '/campagne/:id' },
  { id: 'montee-niveau', pattern: '/campagne/:id/montee-niveau/:pid' },
  { id: 'table', pattern: '/table' },
  { id: 'mj', pattern: '/mj' },
  { id: 'options', pattern: '/options' },
  { id: 'login', pattern: '/login' },
  { id: 'dashboard', pattern: '/dashboard' },
];

export interface Route {
  readonly id: RouteId;
  readonly path: string; // "/profils/guerrier"
  readonly params: Readonly<Record<string, string>>;
  readonly query: Readonly<Record<string, string>>;
}

const NOT_FOUND: Route = { id: 'not-found', path: '', params: {}, query: {} };

/**
 * Parse une chaîne de hash (ex: "#/profils/guerrier?famille=combattants").
 * Retourne toujours une Route (not-found si non reconnu).
 */
export function parseHash(hash: string): Route {
  // Retire le "#" initial. Accepte aussi "#/..." ou vide.
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const [pathPart = '', queryPart = ''] = raw.split('?');

  // Normalise : "//" -> "/", trim trailing "/"
  const path = pathPart.replace(/\/+/g, '/').replace(/\/+$/, '');

  // Matche contre les routes déclarées
  for (const def of ROUTES) {
    const params = matchPattern(def.pattern, path);
    if (params !== null) {
      return {
        id: def.id,
        path,
        params,
        query: parseQuery(queryPart),
      };
    }
  }
  return { ...NOT_FOUND, path };
}

function matchPattern(pattern: string, path: string): Record<string, string> | null {
  const patternSegs = pattern.split('/').filter(Boolean);
  const pathSegs = path.split('/').filter(Boolean);
  if (pattern === '' && path === '') return {};
  if (patternSegs.length !== pathSegs.length) return null;

  const params: Record<string, string> = {};
  for (let i = 0; i < patternSegs.length; i++) {
    const ps = patternSegs[i];
    const vs = pathSegs[i];
    if (!ps || !vs) return null;
    if (ps.startsWith(':')) {
      params[ps.slice(1)] = decodeURIComponent(vs);
    } else if (ps !== vs) {
      return null;
    }
  }
  return params;
}

function parseQuery(s: string): Record<string, string> {
  const out: Record<string, string> = {};
  if (!s) return out;
  for (const pair of s.split('&')) {
    if (!pair) continue;
    const [k, v = ''] = pair.split('=');
    if (k) out[decodeURIComponent(k)] = decodeURIComponent(v);
  }
  return out;
}

/**
 * Construit une URL hash depuis une RouteId et des paramètres.
 */
export function buildHref(
  id: RouteId,
  params: Readonly<Record<string, string>> = {},
  query: Readonly<Record<string, string>> = {},
): string {
  const def = ROUTES.find((r) => r.id === id);
  if (!def) return '#';
  let path = def.pattern;
  for (const [k, v] of Object.entries(params)) {
    path = path.replace(`:${k}`, encodeURIComponent(v));
  }
  const qs = Object.entries(query)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return `#${path}${qs ? `?${qs}` : ''}`;
}

/**
 * Signal de la route courante. Mis à jour automatiquement sur `hashchange`.
 */
export const routeSignal = signal<Route>(NOT_FOUND);

let initialized = false;

/**
 * Initialise le router : lit la hash courante, écoute hashchange.
 * Appelé une seule fois depuis main.ts.
 */
export function initRouter(): void {
  if (initialized) return;
  initialized = true;
  const sync = (): void => {
    routeSignal.set(parseHash(window.location.hash));
  };
  window.addEventListener('hashchange', sync);
  sync();
}

/**
 * Navigue programmatiquement.
 */
export function navigate(
  id: RouteId,
  params: Readonly<Record<string, string>> = {},
  query: Readonly<Record<string, string>> = {},
): void {
  window.location.hash = buildHref(id, params, query);
}
